import React, { useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import debounce from 'lodash/debounce'
import { useLocation } from 'react-router-dom'

import { IconButton, makeStyles, TextField, useTheme } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import LocationOnIcon from '@material-ui/icons/LocationOn'

import AddressOption from './AddressOption'
import useViewportSizes from '~/components/useViewportSize'
import { useLiterals } from '~/components/Literals'
import track from '~/track'
import api from '~/api'
import { Close } from '@material-ui/icons'

function getAddress(data, type, size) {
  return data.filter(({ types }) => types.find((el) => type.includes(el)))?.[0]?.[size] || ''
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: 10,
  },
  inputIcon: {
    color: theme.palette.primary.main,
    fontSize: 14,
  },
  input: {
    boxShadow: theme.shadows[3],
    borderRadius: 4,
  },
  innerInput: {
    padding: 10,
  },
  paperContent: ({ isMobile }) => ({
    maxHeight: !isMobile && 200,
    overflowY: 'auto',
    padding: 0,
    margin: 0,
    '& li': {
      paddingBlock: 8,
      borderBottom: '1px solid #F5F5F5',
    },
    ...theme.gofind.scrollBar,
  }),
  option: ({ isMobile }) =>
    isMobile
      ? {
          paddingLeft: 0,
        }
      : {},
}))

export default function SearchAddress({ size, label, placeholder, onChangeValue, onChangeLocation }) {
  const theme = useTheme()
  const viewport = useViewportSizes()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const classes = useStyles({ isMobile })
  const config = useSelector((state) => state.config)
  const position = useSelector((state) => state.position)
  const location = useLocation()

  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState([])
  const [isNoOptions, setIsNoOptions] = useState(false)
  const [loading, setLoading] = useState(false)

  const { getLiteral } = useLiterals()

  const promiseOptions = (value) =>
    api
      .searchLocation(value, config, position, location)
      .then((res) => res.map((item) => ({ ...item, label: api.getLabel(item, getLiteral) })))

  const handleReset = () => {
    setOptions([])
    setLoading(false)
    setInputValue('')
  }

  const handleSearch = useMemo(
    () =>
      debounce((q) => {
        setIsNoOptions(false)

        if (!q) {
          setLoading(false)

          return undefined
        }

        if (q.length >= 5) {
          setLoading(true)
          promiseOptions(q).then((result) => {
            if (result.length === 0) {
              api.fetchGeoCodeGoogle(q).then(({ results = [] }) => {
                results = results.map(
                  ({ formatted_address, place_id, address_components, geometry: { location } }) => ({
                    label: formatted_address,
                    locationId: place_id,
                    location,
                    address: {
                      country: getAddress(address_components, 'country', 'long_name'),
                      state: getAddress(address_components, 'administrative_area_level_1', 'short_name'),
                      city: getAddress(address_components, ['locality', 'administrative_area_level_2'], 'long_name'),
                      postalCode: getAddress(address_components, 'postal_code', 'long_name'),
                    },
                  }),
                )

                setOptions(results)
                setIsNoOptions(true)
                setLoading(false)
              })
            } else {
              setOptions(result)
              setIsNoOptions(false)
              setLoading(false)
            }
          })
        }
      }, 1000),
    [],
  )

  const handleInputChange = (e, item, reason) => {
    switch (reason) {
      case 'clear':
        handleReset()
        break
      case 'input':
        if (e.key === 'Enter') e.stopPropagation()

        setInputValue(item)
        break
    }
  }

  const handleSelect = async (_, item, reason) => {
    if (item && reason === 'select-option') {
      try {
        const response = await api.fetchGeoCode(item)

        const location = {
          ...item.address,
          lat: response.position?.lat || response.items[0].position.lat,
          lng: response.position?.lng || response.items[0].position.lng,
          description: item.label,
        }

        track('escolherLocalizacao', { name: item.label, category: 'buscar' })

        onChangeLocation(location)
        setInputValue('')
      } catch (error) {
        console.error('cannot change location', error.message)
      }
    }
  }

  useEffect(() => handleSearch(inputValue), [inputValue])

  useEffect(() => {
    if (!inputValue) {
      setOptions([])
      onChangeValue?.(false)
    }

    if (inputValue) onChangeValue?.(true)
  }, [inputValue])

  return (
    <Autocomplete
      open={inputValue.length >= 5}
      autoComplete
      clearOnBlur
      className={classes.root}
      classes={{ paper: classes.paper, option: classes.option }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
      filterOptions={(x) => x}
      options={options}
      onChange={handleSelect}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      renderInput={(params) => (
        <TextField
          {...params}
          className={classes.input}
          size={size ? size : 'medium'}
          label={label}
          placeholder={placeholder}
          color="primary"
          variant="outlined"
          fullWidth
          InputProps={{
            ...params.InputProps,
            style: { padding: 10 },
            startAdornment: <LocationOnIcon className={classes.inputIcon} />,
            endAdornment: (
              <div>
                {inputValue.length ? (
                  <IconButton size="small" onClick={handleReset}>
                    <Close fontSize="small" />
                  </IconButton>
                ) : null}
              </div>
            ),
          }}
        />
      )}
      ListboxProps={{ className: classes.paperContent }}
      renderOption={(option) => <AddressOption option={option} />}
      loading={loading}
      loadingText={`${getLiteral('1171')}...`}
      noOptionsText={isNoOptions && inputValue.length >= 5 ? getLiteral('2526') : `${getLiteral('1171')}...`}
    />
  )
}
